/* eslint-disable react/jsx-no-useless-fragment */
import { useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IAccountRespSchema,
  IOfferingRes,
  IQuoteOfferingAmendActionSchema,
  IQuoteOfferingDeleteActionSchema,
  IQuoteOfferingRemoveReqSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
  OfferingTypesEnum,
  PriceDisplayEnum,
  QuoteItemAmendmentStatusEnum,
  QuoteOfferingAmendActionOptionEnum,
  QuoteOfferingAmendActionSchema,
  QuoteOfferingDeleteActionOptionEnum,
  QuoteOfferingDeleteActionSchema,
  QuoteOfferingGroupRemovalModeEnum,
  QuoteOfferingProps,
  QuoteOfferingRemovalScopeEnum,
  QuoteOfferingState,
  QuoteOfferingWithChildren,
  RateBillingFrequencyEnum,
  RateUsageBillingFrequencyEnum,
  UseQuoteOfferingAmendV2Return,
} from '@monetize/types/app';
import { isBetween, toDateShort } from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { formatISO } from 'date-fns/formatISO';
import { isBefore } from 'date-fns/isBefore';
import { isSameMonth } from 'date-fns/isSameMonth';
import { parseISO } from 'date-fns/parseISO';
import { startOfMonth } from 'date-fns/startOfMonth';
import isUndefined from 'lodash/isUndefined';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, UseFormReturn, useForm } from 'react-hook-form';
import { BsArrowCounterclockwise } from 'react-icons/bs';
import { FaCaretDown } from 'react-icons/fa';
import { MdChevronRight, MdMoreVert } from 'react-icons/md';
import {
  MBox,
  MButton,
  MCenter,
  MCircularProgress,
  MCustomIconButton,
  MDivider,
  MDropdownActionItem,
  MDropdownActions,
  MFlex,
  MIcon,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import {
  DatePicker,
  DatePickerProps,
} from '../../../../../components/Monetize/DatePicker/DatePicker';
import MPopoverWrapper from '../../../../../components/Monetize/MPopoverWrapper';
import { QuoteOfferingConfigurationDrawer } from '../../../../../components/Quotes/QuoteOfferingConfigurationDrawer';
import { logger } from '../../../../../services/logger';
import {
  getQuoteOfferingsFromParentOfferings,
  getQuoteOfferingsWithChildren,
  isProductNonProrated,
  isSameQuoteOfferingGroup,
} from '../../../../../utils/quotes';
import { useQuoteContext } from '../../quoteContext';
import { getCalculatedBillingAnchorStartDate } from '../../quoteUtils';
import { QuoteOfferingAmount } from './QuoteOfferingAmount';
import { hasUsageProducts } from './quoteOfferingUtils';

export interface QuoteOfferingActionsAmendV2Props extends Pick<
  QuoteOfferingProps,
  | 'quoteOffering'
  | 'isChildOffering'
  | 'childQuoteOfferings'
  | 'onChange'
  | 'parentQuoteOffering'
> {
  account: IAccountRespSchema | Pick<IAccountRespSchema, 'id' | 'accountName'>;
  quoteOfferingState: QuoteOfferingState;
  offering?: IOfferingRes | null;
  quoteOfferingLoading: boolean;
  isOfferingOpen: boolean;
  updateQuoteOffering: (
    id: string,
    data: IQuoteOfferingReqSchema,
  ) => Promise<IQuoteOfferingRespSchema | null>;
  quoteOfferingFormState: UseFormReturn<IQuoteOfferingReqSchema>;
  useQuoteOfferingAmendV2State: UseQuoteOfferingAmendV2Return;
  removeQuoteOffering: (
    data: IQuoteOfferingRemoveReqSchema,
  ) => Promise<IQuoteRespSchema | null>;
  handleAddScheduledChange: ({
    startDate,
    endDate,
    rateId,
    quoteOfferingIdToCopy,
  }: {
    startDate: string;
    endDate?: string;
    rateId: string;
    quoteOfferingIdToCopy?: string;
  }) => Promise<IQuoteOfferingRespSchema | null>;
  quoteOfferings: QuoteOfferingWithChildren[];
  monthInterval: number;
  handleQuoteOfferingUpdate: ({
    priceDisplay,
  }: {
    priceDisplay?: PriceDisplayEnum;
  }) => void;
  offeringRate: IRateResSchema | null;
  quotePrices: IQuotePrice[];
}

export const QuoteOfferingActionsAmendV2 = ({
  account,
  quoteOffering,
  useQuoteOfferingAmendV2State,
  isChildOffering,
  parentQuoteOffering,
  childQuoteOfferings,
  quoteOfferingState,
  quoteOfferingLoading,
  isOfferingOpen,
  removeQuoteOffering,
  onChange: onOfferingChange,
  offering,
  handleAddScheduledChange,
  quoteOfferings,
  handleQuoteOfferingUpdate,
  offeringRate,
  monthInterval,
  quotePrices,
  updateQuoteOffering,
}: QuoteOfferingActionsAmendV2Props) => {
  const { quoteData, quoteStateData, isReadOnly } = useQuoteContext();
  const {
    quote,
    fetchQuote,
    setQuote,
    quoteDataOnInitialLoad,
    updateInitialQuoteData,
  } = quoteData;
  const showQuoteOfferingConfDrawer =
    quoteOffering &&
    quoteOffering.billingFrequency !== RateBillingFrequencyEnum.ONETIME &&
    offering &&
    ![
      OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL,
      OfferingTypesEnum.MIN_COMMIT,
    ].includes(offering.type);
  const isMinCommit = offering?.type === OfferingTypesEnum.MIN_COMMIT;

  const hasUsageProduct = hasUsageProducts(quoteOffering, offering);

  const { offeringLoadingState, setOfferingLoadingState } = quoteStateData;
  const { showRevertToOriginal, handleRevertOriginal } =
    useQuoteOfferingAmendV2State;
  const {
    isNewQuoteOfferingForm,
    isRemoved,
    quoteOfferingGroupStartDate,
    quoteOfferingGroupEndDate,
    isOfferingOnetime,
  } = quoteOfferingState;
  const isOfferingDeleting =
    offeringLoadingState[quoteOffering?.id || '']?.isDeleting ||
    offeringLoadingState[parentQuoteOffering?.id || '']?.isDeleting ||
    false;
  const isOfferingLoading =
    offeringLoadingState[quoteOffering?.id || '']?.isLoading ||
    offeringLoadingState[parentQuoteOffering?.id || '']?.isLoading ||
    false;

  const amendPopoverState = useDisclosure();
  const deletePopoverState = useDisclosure();
  const deleteConfigPopoverState = useDisclosure();
  const closeMenuRef = useRef<(() => void) | null>(null);

  const todayOrStartDate = useMemo(() => {
    const today = new Date();
    const startDate = parseISO(quoteOfferingGroupStartDate!);
    const endDate = parseISO(quoteOfferingGroupEndDate!);
    let calculatedStartDate: Date;

    if (startDate && endDate && isBetween(today, startDate, endDate)) {
      if (isMinCommit || hasUsageProduct) {
        const startDay = startDate.getDate();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const lastDayOfCurrentMonth = new Date(
          currentYear,
          currentMonth + 1,
          0,
        ).getDate();

        const day = Math.min(startDay, lastDayOfCurrentMonth);

        calculatedStartDate = new Date(currentYear, currentMonth, day);
      } else {
        calculatedStartDate = today;
      }
    } else {
      calculatedStartDate = startDate;
    }

    if (
      quote?.billingAnchor?.day &&
      quoteOfferingGroupEndDate &&
      quoteOfferingGroupStartDate &&
      (isMinCommit || hasUsageProduct)
    ) {
      calculatedStartDate = getCalculatedBillingAnchorStartDate(
        quoteOfferingGroupStartDate,
        quoteOfferingGroupEndDate,
        quote.billingAnchor.day,
      );
    }

    return formatISO(calculatedStartDate, { representation: 'date' });
  }, [
    isMinCommit,
    hasUsageProduct,
    quoteOfferingGroupStartDate,
    quoteOfferingGroupEndDate,
  ]);

  const amendFormState = useForm<IQuoteOfferingAmendActionSchema>({
    resolver: zodResolver(QuoteOfferingAmendActionSchema),
    mode: 'onChange',
    defaultValues: {
      date: '',
    },
  });

  const deleteFormState = useForm<IQuoteOfferingDeleteActionSchema>({
    resolver: zodResolver(QuoteOfferingDeleteActionSchema),
    mode: 'onChange',
    defaultValues: {
      date: todayOrStartDate,
    },
  });

  useEffect(() => {
    amendFormState.reset({
      date: todayOrStartDate,
    });
    deleteFormState.reset({
      date: todayOrStartDate,
    });
  }, [todayOrStartDate, amendFormState, deleteFormState]);

  const anyOfferingLoading = Object.values(offeringLoadingState).some(
    (item) => item.isLoading,
  );

  const handleDelete = async (deleteReq?: IQuoteOfferingDeleteActionSchema) => {
    try {
      deletePopoverState.onClose();
      deleteConfigPopoverState.onClose();
      closeMenuRef.current?.();
      if (quoteOffering?.id) {
        setOfferingLoadingState((prevValue) => ({
          ...prevValue,
          [quoteOffering.id]: {
            id: quoteOffering.id,
            isLoading: true,
            isDeleting: true,
          },
        }));

        // in the else of this condition onetime offering deletion is added
        if (
          !isUndefined(deleteReq) &&
          deleteReq?.option === QuoteOfferingDeleteActionOptionEnum.AS_OF &&
          deleteReq?.date
        ) {
          const newQuoteResp = await removeQuoteOffering({
            removal_scope: QuoteOfferingRemovalScopeEnum.GROUP,
            group_removal_mode: QuoteOfferingGroupRemovalModeEnum.TARGET_DATE,
            group_id: quoteOffering.id,
            end_date: formatISO(addDays(parseISO(deleteReq?.date), -1), {
              representation: 'date',
            }),
          });

          // Make the last segment of the offering amendmentStatus to NO_CHANGE from frontend state
          // find quoteOffering with quoteOffering.id. parentOfferingId and manually fill in state
          if (newQuoteResp) {
            // Manually update the last segment locked once delete as of date, requirement from zach
            const quoteOfferingsWithChildren = getQuoteOfferingsWithChildren(
              newQuoteResp.quoteOfferings,
            );
            const foundQuoteOffering = quoteOfferingsWithChildren.find(
              (quoteOff) => quoteOffering.id === quoteOff.quoteOffering.id,
            );
            const lastQuoteOffering =
              (foundQuoteOffering?.children || []).length > 0
                ? foundQuoteOffering?.children[
                    foundQuoteOffering?.children.length - 1
                  ]
                : foundQuoteOffering?.quoteOffering;
            if (lastQuoteOffering) {
              lastQuoteOffering.items.forEach(
                (item) =>
                  (item.amendmentStatusOverwrite =
                    QuoteItemAmendmentStatusEnum.NO_CHANGE),
              );
              newQuoteResp.quoteOfferings = newQuoteResp.quoteOfferings.map(
                (quoteOff) =>
                  quoteOff.id === lastQuoteOffering.id
                    ? lastQuoteOffering
                    : quoteOff,
              );
            }

            updateInitialQuoteData('MERGE', newQuoteResp);
            setQuote(newQuoteResp);
          }
        } else {
          const newQuoteResp = await removeQuoteOffering({
            removal_scope: QuoteOfferingRemovalScopeEnum.GROUP,
            group_removal_mode: QuoteOfferingGroupRemovalModeEnum.CONTRACT,
            group_id: quoteOffering.id,
          });
          if (newQuoteResp) {
            setQuote(newQuoteResp);
          }
        }
        setOfferingLoadingState((prevValue) => {
          const value = { ...prevValue };
          delete value[quoteOffering.id];
          return value;
        });
      }
    } catch (error) {
      logger.error('Error while deleting quote offering ====>', error);
    }
  };

  const handleAmend = async (amendReq: IQuoteOfferingAmendActionSchema) => {
    try {
      amendPopoverState.onClose();
      if (quoteOffering?.id) {
        setOfferingLoadingState((prevValue) => ({
          ...prevValue,
          [quoteOffering.id]: {
            id: quoteOffering.id,
            isLoading: true,
            isDeleting: true,
          },
        }));
        if (
          amendReq.option ===
          QuoteOfferingAmendActionOptionEnum.START_OF_CONTRACT
        ) {
          const newOfferingResponse = {
            ...quoteOffering,
            items: quoteOffering.items.map((item) => ({
              ...item,
              amendmentStatus: QuoteItemAmendmentStatusEnum.UPDATED,
            })),
          };
          // update stored data so that if quote is updated we don't get our current state wiped out
          quoteDataOnInitialLoad.quoteOfferings[quoteOffering.id] =
            newOfferingResponse;
          newOfferingResponse.items.forEach((item) => {
            quoteDataOnInitialLoad.quoteItems[item.id] = item;
          });
          await onOfferingChange(
            'UPDATE',
            newOfferingResponse,
            undefined,
            true,
          );
        } else {
          const allOfferings = [
            quoteOffering,
            ...(childQuoteOfferings || []),
          ].filter((offering) => !!offering) as IQuoteOfferingRespSchema[];

          let shouldCreateScheduleChange = true;
          const foundOfferingIndex = allOfferings.findIndex(
            ({ startDate, endDate }) => {
              if (startDate === amendReq.date) {
                // if offering startDate and amendReq.date is same, we dont have to create a new schedule change
                shouldCreateScheduleChange = false;
                return true;
              } else {
                // else find that belongs in between start/end date
                return (
                  isBefore(parseISO(amendReq.date!), parseISO(endDate)) &&
                  isBefore(parseISO(startDate), parseISO(amendReq.date!))
                );
              }
            },
          );

          if (foundOfferingIndex !== -1) {
            if (shouldCreateScheduleChange) {
              const newQuoteOffering = await handleAddScheduledChange({
                rateId: allOfferings[foundOfferingIndex].rateId,
                startDate: amendReq.date!,
                quoteOfferingIdToCopy: allOfferings[foundOfferingIndex].id,
              });
              // Manual update local state to UPDATE to make the editable state
              newQuoteOffering &&
                quote?.id &&
                (await fetchQuote(quote?.id, (newQuote) => {
                  /**
                   * Before we use quote response from backend, it manipulates to manage the updated status
                   * 1. newQuoteOffering and all it's related quote offering group:  should use the response from backend
                   * 2. All other quote offering: state should remain same.
                   *  */
                  const currentQuoteOfferings =
                    getQuoteOfferingsFromParentOfferings(quoteOfferings);
                  const updatedNewQuoteOffering = {
                    ...newQuoteOffering,
                    items: newQuoteOffering.items.map((item) => ({
                      ...item,
                      ...(isProductNonProrated(offering, item.productId) && {
                        incrementalQuantity: 0,
                      }),
                      amendmentStatus: QuoteItemAmendmentStatusEnum.UPDATED,
                    })),
                  };
                  return {
                    ...newQuote,
                    quoteOfferings: newQuote.quoteOfferings.map((item) => {
                      if (
                        isSameQuoteOfferingGroup(updatedNewQuoteOffering, item)
                      ) {
                        if (item.id === updatedNewQuoteOffering.id) {
                          return updatedNewQuoteOffering;
                        } else {
                          return item;
                        }
                      } else {
                        return (
                          currentQuoteOfferings.find(
                            (currentItem) => currentItem.id === item.id,
                          ) || item
                        );
                      }
                    }),
                  };
                }));
            } else {
              // Manually update the status
              if (foundOfferingIndex === 0) {
                await onOfferingChange(
                  'UPDATE',
                  {
                    ...quoteOffering,
                    items: quoteOffering.items.map((item) => ({
                      ...item,
                      ...(isProductNonProrated(offering, item.productId) && {
                        incrementalQuantity: 0,
                      }),
                      amendmentStatus: QuoteItemAmendmentStatusEnum.UPDATED,
                    })),
                  },
                  undefined,
                  true,
                );
              } else {
                await onOfferingChange(
                  'UPDATE',
                  {
                    ...allOfferings[foundOfferingIndex],
                    items: allOfferings[foundOfferingIndex].items.map(
                      (item) => ({
                        ...item,
                        ...(isProductNonProrated(offering, item.productId) && {
                          incrementalQuantity: 0,
                        }),
                        amendmentStatus: QuoteItemAmendmentStatusEnum.UPDATED,
                      }),
                    ),
                  },
                  undefined,
                  true,
                );
              }
            }
          }
        }

        setOfferingLoadingState((prevValue) => {
          const value = { ...prevValue };
          delete value[quoteOffering.id];
          return value;
        });
      }
    } catch (error) {
      logger.error('Error while amending quote offering ====>', error);
    }
  };
  if (!quote) {
    return null;
  }

  const minDate = parseISO(quote.contractStartDate);
  const maxDate = parseISO(quote.contractEndDate);

  const getDatePickerOptions = (): Partial<DatePickerProps> => {
    const datepickerOptions: Partial<DatePickerProps> = {};
    if (isMinCommit || hasUsageProduct) {
      datepickerOptions.mode =
        offeringRate?.usageBillingFrequency ===
        RateUsageBillingFrequencyEnum.ANNUALLY
          ? 'YEAR'
          : 'MONTH';
      datepickerOptions.interval =
        offeringRate?.usageBillingFrequency ===
        RateUsageBillingFrequencyEnum.ANNUALLY
          ? 1
          : (monthInterval as 1 | 3 | 6);

      datepickerOptions.maxDate = maxDate;
      if (quote?.contractStartDate && isSameMonth(minDate, new Date())) {
        datepickerOptions.minDate = startOfMonth(new Date());
      } else if (quote?.contractStartDate && isBefore(minDate, new Date())) {
        datepickerOptions.minDate = startOfMonth(minDate);
      } else {
        datepickerOptions.minDate = addMonths(minDate, monthInterval);
      }

      if (
        quote?.billingAnchor?.day &&
        quoteOfferingGroupStartDate &&
        quoteOfferingGroupEndDate
      ) {
        datepickerOptions.baseDate = new Date(
          minDate.getFullYear(),
          minDate.getMonth(),
          quote?.billingAnchor?.day,
        );

        datepickerOptions.minDate = getCalculatedBillingAnchorStartDate(
          quoteOfferingGroupStartDate,
          quoteOfferingGroupEndDate,
          quote.billingAnchor.day,
        );
      } else {
        datepickerOptions.baseDate = minDate;
      }
    } else {
      datepickerOptions.minDate = minDate;
      datepickerOptions.maxDate = maxDate;
    }

    return datepickerOptions;
  };

  const datepickerAsOfDateOptions = getDatePickerOptions();

  const deletePopoverBody = (
    <Controller
      name="option"
      control={deleteFormState.control}
      render={({ field: { ...rest } }) => (
        <MRadioGroup {...rest}>
          <MStack columnGap={2}>
            {!isOfferingOnetime && (
              <>
                <MRadio
                  value={QuoteOfferingDeleteActionOptionEnum.AS_OF}
                  alignItems="start"
                >
                  As of
                  <Controller
                    name="date"
                    control={deleteFormState.control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <DatePicker
                        value={value as string}
                        popoverContentProps={{
                          onMouseDown: (e) => e.stopPropagation(),
                        }}
                        onChange={(e) => {
                          onChange(e);
                          deleteFormState.setValue(
                            'option',
                            QuoteOfferingDeleteActionOptionEnum.AS_OF,
                            { shouldValidate: true },
                          );
                        }}
                        {...datepickerAsOfDateOptions}
                        {...rest}
                      />
                    )}
                  />
                </MRadio>
                <MDivider />
              </>
            )}
            <MRadio
              value={QuoteOfferingDeleteActionOptionEnum.START_OF_CONTRACT}
              alignItems="start"
            >
              <MText>From Start of Contract</MText>
              <MText color="tGray.darkPurple" fontSize="xs">
                {toDateShort(quote.contractStartDate)}
              </MText>
            </MRadio>
            <MDivider />
            <MBox display="flex" justifyContent="end">
              <MButton
                variant="primary"
                size="sm"
                minW="auto"
                isDisabled={!deleteFormState.formState.isValid}
                onClick={deleteFormState.handleSubmit(handleDelete)}
              >
                Save
              </MButton>
            </MBox>
          </MStack>
        </MRadioGroup>
      )}
    />
  );

  if (isChildOffering || isNewQuoteOfferingForm) {
    return null;
  }

  /** Covers the row while the offering is being deleted, in either mode */
  const deletingOverlay = isOfferingDeleting && (
    <MBox
      position="absolute"
      top={0}
      left={0}
      width="100%"
      height="100%"
      backgroundColor="white"
      opacity={0.75}
    >
      <MCenter height="100%" justifyContent="flex-end" pr="1">
        <MCircularProgress isIndeterminate size="2xs" />
      </MCenter>
    </MBox>
  );

  /**
   * Mirrors new/renewal quotes: a collapsed row shows only the offering total
   * under the Amount column, and the actions appear once it is expanded.
   */
  if (!isOfferingOpen) {
    return (
      <MFlex alignItems="center" gridColumn="1 / -1" justifyContent="flex-end">
        {deletingOverlay}
        <QuoteOfferingAmount
          childQuoteOfferings={childQuoteOfferings}
          quoteOffering={quoteOffering}
          currency={quote.currency}
        />
      </MFlex>
    );
  }

  return (
    <>
      <MFlex alignItems="center" gridColumn="1 / -1" justifyContent="flex-end">
        {deletingOverlay}

        {!isReadOnly && (
          <>
            {showRevertToOriginal && !isChildOffering ? (
              <MButton
                variant="tertiary"
                onClick={handleRevertOriginal}
                isDisabled={quoteOfferingLoading || anyOfferingLoading}
              >
                <MIcon as={BsArrowCounterclockwise} mr="1" boxSize={4} />
                Revert to Original
              </MButton>
            ) : (
              <>
                {!isOfferingOnetime && (
                  <MPopoverWrapper
                    isOpen={amendPopoverState.open}
                    placement="bottom"
                    onOpen={amendPopoverState.onOpen}
                    onClose={amendPopoverState.onClose}
                    usePortal
                    triggerContent={
                      <MButton
                        variant="tertiary"
                        minW="auto"
                        onClick={(ev) => {
                          ev.stopPropagation();
                        }}
                        isDisabled={quoteOfferingLoading || anyOfferingLoading}
                      >
                        Amend
                        <MIcon as={FaCaretDown} ml="1" boxSize={4} />
                      </MButton>
                    }
                    popoverContentProps={{ maxW: 210 }}
                    bodyContent={
                      <Controller
                        name="option"
                        control={amendFormState.control}
                        render={({ field: { ...rest } }) => (
                          <MRadioGroup {...rest}>
                            <MStack columnGap={2}>
                              <MRadio
                                value={QuoteOfferingAmendActionOptionEnum.AS_OF}
                                alignItems="start"
                              >
                                As of
                                <Controller
                                  name="date"
                                  control={amendFormState.control}
                                  render={({
                                    field: { value, onChange, ...rest },
                                  }) => (
                                    <DatePicker
                                      value={value as string}
                                      popoverContentProps={{
                                        onMouseDown: (e) => e.stopPropagation(),
                                      }}
                                      onChange={(e) => {
                                        onChange(e);
                                        amendFormState.setValue(
                                          'option',
                                          QuoteOfferingAmendActionOptionEnum.AS_OF,
                                          { shouldValidate: true },
                                        );
                                      }}
                                      {...datepickerAsOfDateOptions}
                                      {...rest}
                                    />
                                  )}
                                />
                              </MRadio>
                              <MDivider />
                              <MRadio
                                value={
                                  QuoteOfferingAmendActionOptionEnum.START_OF_CONTRACT
                                }
                                alignItems="start"
                              >
                                <MText>From Start of Contract</MText>
                                <MText color="tGray.darkPurple" fontSize="xs">
                                  {toDateShort(quote.contractStartDate)}
                                </MText>
                              </MRadio>
                              <MDivider />
                              <MBox display="flex" justifyContent="end">
                                <MButton
                                  variant="primary"
                                  size="sm"
                                  minW="auto"
                                  isDisabled={!amendFormState.formState.isValid}
                                  onClick={amendFormState.handleSubmit(
                                    handleAmend,
                                  )}
                                >
                                  Save
                                </MButton>
                              </MBox>
                            </MStack>
                          </MRadioGroup>
                        )}
                      />
                    }
                  />
                )}

                {isOfferingOnetime ? (
                  <MButton
                    variant="deleteOutline"
                    minW="auto"
                    border="none"
                    onClick={() => {
                      handleDelete();
                    }}
                    isDisabled={quoteOfferingLoading || anyOfferingLoading}
                    bgColor="transparent"
                  >
                    Delete
                    {/* Reserve the caret's width so the label lines up with the subscription rows' Delete */}
                    <MIcon
                      as={FaCaretDown}
                      ml="1"
                      boxSize={4}
                      visibility="hidden"
                      aria-hidden
                    />
                  </MButton>
                ) : (
                  <MPopoverWrapper
                    isOpen={deletePopoverState.open}
                    placement="bottom"
                    onOpen={deletePopoverState.onOpen}
                    onClose={deletePopoverState.onClose}
                    usePortal
                    triggerContent={
                      <MButton
                        variant="deleteOutline"
                        minW="auto"
                        border="none"
                        onClick={(ev) => {
                          ev.stopPropagation();
                        }}
                        isDisabled={quoteOfferingLoading || anyOfferingLoading}
                        bgColor="transparent"
                      >
                        Delete
                        <MIcon as={FaCaretDown} ml="1" boxSize={4} />
                      </MButton>
                    }
                    popoverContentProps={{ maxW: 210 }}
                    bodyContent={deletePopoverBody}
                  />
                )}
              </>
            )}
          </>
        )}

        {/* Ensure that we have space here even if the menu is not rendered */}
        <MBox minW="24px">
          {(showQuoteOfferingConfDrawer ||
            (!isRemoved && showRevertToOriginal && !isReadOnly)) && (
            <MDropdownActions
              popOverContentProps={{ minW: '210px' }}
              popoverProps={{
                unmountOnExit: false,
                closeOnSelect: false,
              }}
              menuButtonProps={{ asChild: true }}
              renderTrigger={() => (
                <MCustomIconButton
                  variant="icon"
                  btnSize={4}
                  containerSize={6}
                  p={3}
                  icon={MdMoreVert}
                  aria-label="More actions"
                  isDisabled={quoteOfferingLoading || anyOfferingLoading}
                />
              )}
            >
              {({ onClose: closeMenu }) => {
                closeMenuRef.current = closeMenu;
                return (
                  <>
                    {showQuoteOfferingConfDrawer && (
                      <QuoteOfferingConfigurationDrawer
                        account={account}
                        renderTrigger={({ onOpen, isDisabled }) => (
                          <MDropdownActionItem
                            onClick={() => {
                              onOpen();
                              closeMenu();
                            }}
                            isDisabled={isDisabled || isRemoved}
                          >
                            <MText color="inherit">Configure settings</MText>
                          </MDropdownActionItem>
                        )}
                        isDisabled={quoteOfferingLoading || anyOfferingLoading}
                        onOfferingChange={onOfferingChange}
                        quoteOffering={quoteOffering}
                        isReadOnly={isReadOnly}
                        handleQuoteOfferingUpdate={handleQuoteOfferingUpdate}
                        offering={offering}
                        offeringRate={offeringRate}
                        quote={quote}
                        isOfferingLoading={isOfferingLoading}
                        quotePrices={quotePrices}
                        updateQuoteOffering={updateQuoteOffering}
                      />
                    )}

                    {!isRemoved && showRevertToOriginal && !isReadOnly && (
                      <MPopoverWrapper
                        isOpen={deleteConfigPopoverState.open}
                        placement="right"
                        onOpen={deleteConfigPopoverState.onOpen}
                        onClose={deleteConfigPopoverState.onClose}
                        triggerContent={
                          <MDropdownActionItem
                            onClick={() => {}}
                            color="tRed.base"
                          >
                            <MFlex
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <MText color="inherit">Delete</MText>
                              <MIcon as={MdChevronRight} boxSize={4} />
                            </MFlex>
                          </MDropdownActionItem>
                        }
                        popoverContentProps={{ maxW: 210 }}
                        popoverProps={{ closeOnInteractOutside: false }}
                        bodyContent={deletePopoverBody}
                      />
                    )}
                  </>
                );
              }}
            </MDropdownActions>
          )}
        </MBox>
      </MFlex>
      {/* Expanded, the offering total stays in the header under the actions,
          spanning every column but the trailing one so it ends in the Amount
          column like the product prices */}
      <MFlex gridColumn="1 / -2" justifyContent="flex-end">
        <QuoteOfferingAmount
          childQuoteOfferings={childQuoteOfferings}
          quoteOffering={quoteOffering}
          currency={quote.currency}
        />
      </MFlex>
    </>
  );
};
